import { Box, Typography } from '@mui/material'
import React from 'react'
import { Control, House } from '../../types'
import { isActive } from '../../utils/businessUtil'

// 使用中のピン番号を集計する（表示は 1-16、内部は 0-15）
export const getUsedPins = (
	controls: Control[],
): { usedPins: Set<number>; twayPins: Set<number> } => {
	const usedPins = new Set<number>()
	const twayPins = new Set<number>()

	controls.forEach((control) => {
		const isTway = control.category === 'tway'

		if (control.activePin !== null && control.activePin >= 0) {
			const displayPin = control.activePin + 1
			usedPins.add(displayPin)
			if (isTway) twayPins.add(displayPin)
		}

		if (control.negativePin !== null && control.negativePin >= 0) {
			const displayPin = control.negativePin + 1
			usedPins.add(displayPin)
			if (isTway) twayPins.add(displayPin)
		}
	})

	return { usedPins, twayPins }
}

type PinIndicatorProps = {
	usedPins: Set<number> // 他の機器が使用中のピン
	twayPins: Set<number>
	house?: House
	selectedPins?: Set<number> // この機器で選択中のピン
	onPinClick?: (pin: number) => void // クリックで選択/解除
}

// ピン 1-16 の状態を可視化する共有コンポーネント。
// 使用可能（クリックで選択）/ 使用中（他機器）/ 選択中（この機器）を色分けする。
export const PinIndicator: React.FC<PinIndicatorProps> = ({
	usedPins,
	twayPins,
	house,
	selectedPins,
	onPinClick,
}) => {
	const pins = Array.from({ length: 16 }, (_, i) => i + 1)

	return (
		<Box sx={{ mt: 0.5 }}>
			<Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
				{pins.map((pin) => {
					const isSelected = selectedPins?.has(pin) ?? false
					const isUsed = usedPins.has(pin) && !isSelected
					const isTwayPin = twayPins.has(pin) && !isSelected
					const isPinOn = house ? isActive(house.pin, pin - 1) : false
					const clickable = Boolean(onPinClick) && !isUsed

					// 状態別スタイル
					let styles: object
					if (isSelected) {
						styles = {
							bgcolor: 'primary.main',
							color: 'primary.contrastText',
							borderColor: 'primary.main',
							fontWeight: 700,
						}
					} else if (isUsed) {
						styles = {
							bgcolor: 'action.disabledBackground',
							color: 'text.disabled',
							borderColor: 'divider',
							...(isTwayPin ? { borderColor: 'success.main' } : {}),
						}
					} else {
						// 使用可能
						styles = {
							bgcolor: 'background.paper',
							color: 'text.primary',
							borderColor: 'divider',
							...(clickable
								? {
										'&:hover': {
											bgcolor: 'action.hover',
											borderColor: 'primary.main',
										},
								  }
								: {}),
						}
					}

					const status = isSelected
						? '選択中'
						: isTwayPin
						? '三方弁で使用中'
						: isUsed
						? '使用中（他機器）'
						: '使用可能' + (clickable ? '（クリックで選択）' : '')

					return (
						<Box
							key={pin}
							onClick={clickable ? () => onPinClick?.(pin) : undefined}
							title={`ピン${pin}: ${status}${isPinOn ? ' / 現在ON' : ''}`}
							sx={{
								position: 'relative',
								width: 30,
								height: 30,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: 12,
								fontVariantNumeric: 'tabular-nums',
								borderRadius: '6px',
								border: '1.5px solid',
								cursor: clickable ? 'pointer' : 'default',
								transition: 'all .15s ease',
								...styles,
							}}
						>
							{pin}
							{isPinOn && (
								<Box
									sx={{
										position: 'absolute',
										top: 2,
										right: 2,
										width: 6,
										height: 6,
										borderRadius: '50%',
										bgcolor: 'warning.main',
									}}
								/>
							)}
						</Box>
					)
				})}
			</Box>

			{/* 凡例 */}
			<Box
				sx={{
					display: 'flex',
					gap: 1.5,
					flexWrap: 'wrap',
					mt: 0.75,
				}}
			>
				<LegendItem
					sx={{ bgcolor: 'primary.main', borderColor: 'primary.main' }}
					label="選択中"
				/>
				<LegendItem
					sx={{ bgcolor: 'background.paper', borderColor: 'divider' }}
					label={onPinClick ? '使用可能（クリックで選択）' : '使用可能'}
				/>
				<LegendItem
					sx={{
						bgcolor: 'action.disabledBackground',
						borderColor: 'divider',
					}}
					label="使用中（他機器）"
				/>
			</Box>
		</Box>
	)
}

const LegendItem = ({ sx, label }: { sx: object; label: string }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
		<Box
			sx={{
				width: 14,
				height: 14,
				borderRadius: '4px',
				border: '1.5px solid',
				...sx,
			}}
		/>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
	</Box>
)
